import React from "react";
import {
  Grid3X3,
  Box,
  Globe,
  MapPin,
  Camera,
  GitMerge,
  Spline,
  Plus,
  Copy,
  Eye,
  EyeOff,
  ScanSearch,
  Trash2,
} from "lucide-react";
import {
  JointIcon,
  ExplosionIcon,
  BoxGeomIcon,
  SphereGeomIcon,
  CapsuleGeomIcon,
  CylinderGeomIcon,
} from "../../ui/Icons";
import Tooltip from "../../ui/Tooltip";
import OrderedIconActionContextRow from "../../ui/OrderedIconActionContextRow";
import DeleteMenuContent from "../../ui/DeleteMenuContent";
import DuplicateMenuContent from "../../ui/DuplicateMenuContent";
import { stripNamespacePrefix as stripPrefix } from "../../../utils/nameUtils";
import { ReparentMenuIcon } from "./icons";
export default function SceneGraphBodyContextMenu({
  contextMenuBodyUuids,
  effectiveMoveBodyUuids,
  invalidBatchParentUuids,
  deleteSubmenuOpen,
  duplicateSubmenuOpen,
  addNewSubmenuOpen,
  addJointKindSubmenuOpen,
  setAddJointKindSubmenuOpen,
  addGeomKindSubmenuOpen,
  setAddGeomKindSubmenuOpen,
  addVisualGeomKindSubmenuOpen,
  setAddVisualGeomKindSubmenuOpen,
  reparentSubmenuOpen,
  setBodyActionSubmenuKey,
  setContextMenuOpen,
  isPhysicsRunning,
  groupedMoveParentOptions,
  bodies,
  viewerActions,
  bodyVisibility,
  inspectContextMenuTarget,
  handleAddChildBody,
  handleAddJointToBody,
  handleAddSiteToBody,
  handleAddGeomToBody,
  handleAddVisualGeomToBody,
  handleAddConstraintForBody,
  handleAddTendonForJoint,
  handleAddCameraToBody,
  handleDuplicateBody,
  handleDeleteBodyObjects,
  handleDeleteBodies,
  handleMoveBodies,
  getJointAddOptionsForBody,
  getPreferredTendonJointForBody,
}) {
  return (
    <>
      {contextMenuBodyUuids.length > 1 ? (
        <div className="px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
          {contextMenuBodyUuids.length} bodies selected
        </div>
      ) : null}
      {contextMenuBodyUuids.length > 1 &&
      effectiveMoveBodyUuids.length > 0 &&
      effectiveMoveBodyUuids.length !== contextMenuBodyUuids.length ? (
        <div className="px-2 pb-1 text-[10px] panel-text-helper">
          Reparent moves {effectiveMoveBodyUuids.length} top-level{" "}
          {effectiveMoveBodyUuids.length === 1 ? "body" : "bodies"}.
        </div>
      ) : null}
      <div className="px-2 pb-1.5">
        <OrderedIconActionContextRow
          activeSubmenuKey={
            deleteSubmenuOpen
              ? "delete"
              : reparentSubmenuOpen
                ? "reparent"
                : addNewSubmenuOpen
                  ? "addNew"
                  : duplicateSubmenuOpen
                    ? "duplicate"
                    : null
          }
          setActiveSubmenuKey={setBodyActionSubmenuKey}
          submenuClassName="mt-1 min-w-[180px] rounded-md border border-gray-600 p-0.5 shadow-xl"
          submenuStyle={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
          actions={[
            {
              key: "inspect",
              label: "Inspect",
              icon: <ScanSearch className="w-4 h-4" aria-hidden="true" />,
              onDefaultClick: inspectContextMenuTarget,
            },
            (() => {
              const allBodyVisible = contextMenuBodyUuids.every(
                (uuid) => bodyVisibility[uuid] !== false
              );
              return {
                key: "visibility",
                label: allBodyVisible ? "Hide" : "Show",
                icon: allBodyVisible ? (
                  <EyeOff className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <Eye className="w-4 h-4" aria-hidden="true" />
                ),
                disabled: isPhysicsRunning,
                disabledByPhysics: isPhysicsRunning,
                onDefaultClick: () => {
                  if (isPhysicsRunning) return;
                  contextMenuBodyUuids.forEach((uuid) =>
                    viewerActions.setBodyVisibility(uuid, !allBodyVisible)
                  );
                  setContextMenuOpen(false);
                },
              };
            })(),
            {
              key: "delete",
              label: "Delete",
              icon: <Trash2 className="w-4 h-4" aria-hidden="true" />,
              disabled: isPhysicsRunning,
              disabledByPhysics: isPhysicsRunning,
              onDefaultClick: () => {
                if (isPhysicsRunning) return;
                handleDeleteBodies(contextMenuBodyUuids);
              },
              submenu: (
                <DeleteMenuContent
                  onDeleteBody={() => handleDeleteBodies(contextMenuBodyUuids)}
                  onDeleteObject={() => handleDeleteBodyObjects(contextMenuBodyUuids)}
                  bodyLabel={
                    contextMenuBodyUuids.length === 1 ? "Delete body" : "Delete selected bodies"
                  }
                  objectLabel={
                    contextMenuBodyUuids.length === 1
                      ? "Delete entire object"
                      : "Delete selected objects"
                  }
                />
              ),
            },
            {
              key: "reparent",
              label: "Reparent",
              icon: <ReparentMenuIcon />,
              disabled: isPhysicsRunning || effectiveMoveBodyUuids.length === 0,
              disabledByPhysics: isPhysicsRunning,
              submenu: (
                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={() => handleMoveBodies(null)}
                    className="w-full text-left flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-700/90 transition-colors"
                  >
                    <Globe className="w-4 h-4 shrink-0" aria-hidden="true" />
                    World
                  </button>
                  <div className="border-t border-gray-700/80 pt-1">
                    <div className="max-h-48 overflow-y-auto space-y-0.5">
                      {groupedMoveParentOptions
                        ? groupedMoveParentOptions.map((group) => (
                            <div key={group.label}>
                              <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                                {group.label}
                              </div>
                              {group.bodies.map((body) => (
                                <button
                                  key={body.uuid}
                                  type="button"
                                  onClick={() => handleMoveBodies(body.uuid)}
                                  className="w-full text-left rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-700/90 transition-colors"
                                >
                                  {stripPrefix(body.name)}
                                </button>
                              ))}
                            </div>
                          ))
                        : Object.values(bodies || {})
                            .filter((body) => body?.uuid && !invalidBatchParentUuids.has(body.uuid))
                            .sort((a, b) =>
                              String(stripPrefix(a.name) || "").localeCompare(
                                String(stripPrefix(b.name) || "")
                              )
                            )
                            .map((body) => (
                              <button
                                key={body.uuid}
                                type="button"
                                onClick={() => handleMoveBodies(body.uuid)}
                                className="w-full text-left rounded-sm px-2 py-1.5 text-sm text-gray-200 hover:bg-gray-700/90 transition-colors"
                              >
                                {stripPrefix(body.name)}
                              </button>
                            ))}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: "addNew",
              label: "Add new",
              icon: <Plus className="w-4 h-4" aria-hidden="true" />,
              disabled: isPhysicsRunning || contextMenuBodyUuids.length !== 1,
              disabledByPhysics: isPhysicsRunning,
              onDefaultClick: () => {
                if (isPhysicsRunning || contextMenuBodyUuids.length !== 1) return;
                handleAddChildBody(contextMenuBodyUuids[0]);
              },
              submenu: !(isPhysicsRunning || contextMenuBodyUuids.length !== 1) ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <Tooltip content="Child body">
                      <button
                        type="button"
                        aria-label="Child body"
                        onClick={() => handleAddChildBody(contextMenuBodyUuids[0])}
                        onMouseEnter={() => {
                          setAddJointKindSubmenuOpen(false);
                          setAddGeomKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                      >
                        <Box className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Visual">
                      <button
                        type="button"
                        aria-label="Visual"
                        onClick={() => handleAddVisualGeomToBody(contextMenuBodyUuids[0], "box")}
                        onMouseEnter={() => {
                          setAddVisualGeomKindSubmenuOpen(true);
                          setAddJointKindSubmenuOpen(false);
                          setAddGeomKindSubmenuOpen(false);
                        }}
                        className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                      >
                        <Grid3X3 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                    {(() => {
                      const jointOptions = getJointAddOptionsForBody(contextMenuBodyUuids[0]);
                      const firstEnabledJointOption = jointOptions.find(
                        (option) => !option.disabled
                      );
                      const jointDisabledReason =
                        firstEnabledJointOption == null
                          ? jointOptions.find((option) => option.description)?.description ||
                            "No joint types can be added here."
                          : null;
                      const jointDisabled = !firstEnabledJointOption;
                      return (
                        <Tooltip content={jointDisabledReason || "Joint"}>
                          <button
                            type="button"
                            aria-label="Joint"
                            aria-disabled={jointDisabled ? "true" : undefined}
                            onClick={() => {
                              if (jointDisabled) return;
                              handleAddJointToBody(
                                contextMenuBodyUuids[0],
                                firstEnabledJointOption.kind
                              );
                            }}
                            onMouseEnter={() => {
                              setAddJointKindSubmenuOpen(!jointDisabled);
                              setAddGeomKindSubmenuOpen(false);
                              setAddVisualGeomKindSubmenuOpen(false);
                            }}
                            className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                              jointDisabled
                                ? "text-gray-300 opacity-60 cursor-not-allowed"
                                : "text-gray-200 hover:bg-gray-700/90"
                            }`}
                          >
                            <JointIcon className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      );
                    })()}
                    <Tooltip content="Site">
                      <button
                        type="button"
                        aria-label="Site"
                        onClick={() => handleAddSiteToBody(contextMenuBodyUuids[0])}
                        onMouseEnter={() => {
                          setAddJointKindSubmenuOpen(false);
                          setAddGeomKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                      >
                        <MapPin className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Collision">
                      <button
                        type="button"
                        aria-label="Collision"
                        onClick={() => handleAddGeomToBody(contextMenuBodyUuids[0], "sphere")}
                        onMouseEnter={() => {
                          setAddGeomKindSubmenuOpen(true);
                          setAddJointKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                      >
                        <ExplosionIcon className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Constraint">
                      <button
                        type="button"
                        aria-label="Constraint"
                        onClick={() => handleAddConstraintForBody(contextMenuBodyUuids[0])}
                        onMouseEnter={() => {
                          setAddJointKindSubmenuOpen(false);
                          setAddGeomKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                      >
                        <GitMerge className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                    {(() => {
                      const tendonJointUuid = getPreferredTendonJointForBody(
                        contextMenuBodyUuids[0]
                      );
                      const tendonDisabled = !tendonJointUuid;
                      return (
                        <Tooltip
                          content={
                            tendonDisabled
                              ? "Add a hinge or slide joint before adding a tendon"
                              : "Tendon"
                          }
                        >
                          <button
                            type="button"
                            aria-label="Tendon"
                            aria-disabled={tendonDisabled}
                            onClick={() => {
                              if (tendonDisabled) return;
                              handleAddTendonForJoint(tendonJointUuid);
                            }}
                            onMouseEnter={() => {
                              setAddJointKindSubmenuOpen(false);
                              setAddGeomKindSubmenuOpen(false);
                              setAddVisualGeomKindSubmenuOpen(false);
                            }}
                            className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                              tendonDisabled
                                ? "text-gray-300 opacity-60 cursor-not-allowed"
                                : "text-gray-200 hover:bg-gray-700/90"
                            }`}
                          >
                            <Spline className="w-4 h-4" aria-hidden="true" />
                          </button>
                        </Tooltip>
                      );
                    })()}
                    <Tooltip content="Camera">
                      <button
                        type="button"
                        aria-label="Camera"
                        onClick={() => handleAddCameraToBody(contextMenuBodyUuids[0])}
                        onMouseEnter={() => {
                          setAddJointKindSubmenuOpen(false);
                          setAddGeomKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                      >
                        <Camera className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                  </div>
                  {addJointKindSubmenuOpen &&
                    getJointAddOptionsForBody(contextMenuBodyUuids[0]).some(
                      (option) => !option.disabled
                    ) && (
                      <div
                        className="rounded-md border border-gray-600 p-1 shadow-xl"
                        style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
                      >
                        <div className="flex items-center gap-1">
                          {getJointAddOptionsForBody(contextMenuBodyUuids[0]).map(
                            ({ kind, label, Icon, disabled, description }) => (
                              <Tooltip key={kind} content={description || label}>
                                <button
                                  type="button"
                                  aria-label={label}
                                  aria-disabled={disabled ? "true" : undefined}
                                  onClick={() => {
                                    if (disabled) return;
                                    handleAddJointToBody(contextMenuBodyUuids[0], kind);
                                  }}
                                  className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                                    disabled
                                      ? "text-gray-300 opacity-60 cursor-not-allowed"
                                      : "text-gray-200 hover:bg-gray-700/90"
                                  }`}
                                >
                                  <Icon className="w-4 h-4" aria-hidden="true" />
                                </button>
                              </Tooltip>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  {addVisualGeomKindSubmenuOpen && (
                    <div
                      className="rounded-md border border-gray-600 p-1 shadow-xl"
                      style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
                    >
                      <div className="flex items-center gap-1">
                        {[
                          { kind: "box", label: "Box", Icon: BoxGeomIcon },
                          { kind: "sphere", label: "Sphere", Icon: SphereGeomIcon },
                          { kind: "capsule", label: "Capsule", Icon: CapsuleGeomIcon },
                          {
                            kind: "cylinder",
                            label: "Cylinder",
                            Icon: CylinderGeomIcon,
                          },
                          { kind: "mesh", label: "Mesh", Icon: Grid3X3 },
                        ].map(({ kind, label, Icon }) => (
                          <Tooltip key={kind} content={label}>
                            <button
                              type="button"
                              aria-label={label}
                              onClick={() =>
                                handleAddVisualGeomToBody(contextMenuBodyUuids[0], kind)
                              }
                              className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                            >
                              <Icon className="w-4 h-4" aria-hidden="true" />
                            </button>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  )}
                  {addGeomKindSubmenuOpen && (
                    <div
                      className="rounded-md border border-gray-600 p-1 shadow-xl"
                      style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
                    >
                      <div className="flex items-center gap-1">
                        {[
                          { kind: "box", label: "Box", Icon: BoxGeomIcon },
                          { kind: "sphere", label: "Sphere", Icon: SphereGeomIcon },
                          { kind: "capsule", label: "Capsule", Icon: CapsuleGeomIcon },
                          {
                            kind: "cylinder",
                            label: "Cylinder",
                            Icon: CylinderGeomIcon,
                          },
                        ].map(({ kind, label, Icon }) => (
                          <Tooltip key={kind} content={label}>
                            <button
                              type="button"
                              aria-label={label}
                              onClick={() => handleAddGeomToBody(contextMenuBodyUuids[0], kind)}
                              className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                            >
                              <Icon className="w-4 h-4" aria-hidden="true" />
                            </button>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null,
            },
            {
              key: "duplicate",
              label: "Duplicate",
              icon: <Copy className="w-4 h-4" aria-hidden="true" />,
              disabled: isPhysicsRunning || contextMenuBodyUuids.length !== 1,
              disabledByPhysics: isPhysicsRunning,
              onDefaultClick: () => {
                if (isPhysicsRunning || contextMenuBodyUuids.length !== 1) return;
                handleDuplicateBody(contextMenuBodyUuids[0], false);
              },
              submenu: !(isPhysicsRunning || contextMenuBodyUuids.length !== 1) ? (
                <DuplicateMenuContent
                  onDuplicateBody={() => handleDuplicateBody(contextMenuBodyUuids[0], false)}
                  onDuplicateBranch={() => handleDuplicateBody(contextMenuBodyUuids[0], true)}
                />
              ) : null,
            },
          ]}
        />
      </div>
    </>
  );
}
